import classNames from 'classnames';
import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Segmented, StateMessage } from '@/components/ui/controls';
import { NotYet, NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, PageHeader } from '@/components/ui/page';
import { formatDateTime, formatRelative, initials } from '@/utils/format';
import { useApi } from '@/utils/use-api';

import ChannelNav from './channel-nav';
import { useClosing } from '@/utils/use-closing';

/** A mail Prisma read from the contact mailbox (GET /inbound-emails). */
type InboundEmail = {
  id: number;
  from_address: string;
  from_name: string;
  reply_to: string;
  subject: string;
  body: string;
  received_at: string;
  kind: '' | 'contact_form' | 'email';
  lead_id: string;
  lead_email: string;
  lead_name: string;
  processed_at: string | null;
  error: string;
  answers: { question: string; answer: string }[];
};

/** The mail of one lead, or of one sender who is not a lead, oldest first. */
type Thread = { key: string; name: string; contact: string; leadId: string; mails: InboundEmail[]; last: InboundEmail };

type Filter = 'all' | 'form' | 'email';

const threadsOf = (mails: InboundEmail[]): Thread[] => {
  const byKey = new Map<string, InboundEmail[]>();
  for (const m of mails) {
    const key = m.lead_id ? `lead:${m.lead_id}` : `from:${m.reply_to || m.from_address}`;
    byKey.set(key, [...(byKey.get(key) ?? []), m]);
  }
  return [...byKey.entries()]
    .map(([key, list]) => {
      const sorted = [...list].sort((a, b) => a.received_at.localeCompare(b.received_at));
      const last = sorted[sorted.length - 1];
      const contact = last.lead_email || last.reply_to || last.from_address;
      return { key, name: last.lead_name || (last.kind === 'contact_form' ? contact : last.from_name) || contact, contact, leadId: last.lead_id, mails: sorted, last };
    })
    .sort((a, b) => b.last.received_at.localeCompare(a.last.received_at));
};

const isForm = (t: Thread) => t.mails.some((m) => m.kind === 'contact_form');

/** One mail: the form's answers as a list, or the text as written. */
const MailCard = ({ mail }: { mail: InboundEmail }) => (
  <li className="rounded-lg bg-surface-inset px-3 py-2.5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Badge tone={mail.kind === 'contact_form' ? 'brand' : 'neutral'}>{mail.kind === 'contact_form' ? 'Contact form' : 'Email'}</Badge>
      <span className="text-xs text-fg-faint">{formatDateTime(mail.received_at)}</span>
    </div>
    {mail.subject && <p className="mt-1.5 text-sm text-fg-strong">{mail.subject}</p>}
    {mail.answers.length > 0 ? (
      <dl className="mt-1.5 space-y-1">
        {mail.answers.map((a) => (
          <div key={a.question} className="grid grid-cols-1 gap-0.5 text-sm sm:grid-cols-[140px_1fr] sm:gap-3">
            <dt className="text-fg-muted">{a.question}</dt>
            <dd className="min-w-0 whitespace-pre-wrap break-words text-fg">{a.answer || '—'}</dd>
          </div>
        ))}
      </dl>
    ) : (
      <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-fg">{mail.body}</p>
    )}
    {mail.error && <p className="mt-1.5 text-xs text-danger">{mail.error}</p>}
  </li>
);

/** One thread beside the list (full screen on a phone): who wrote, the lead it made, every mail. */
const ThreadPanel = ({ thread, onClose }: { thread: Thread; onClose: () => void }) => {
  const [closing, close] = useClosing(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <aside aria-label="Email thread" className={`fixed inset-y-0 right-0 z-40 flex w-full max-w-[440px] flex-col bg-surface-raised shadow-e3 ${closing ? 'animate-drawer-out-right' : 'animate-drawer-in-right'}`}>
      <div className="flex items-start gap-3 px-5 py-4">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-fg-strong">{thread.name}</p>
          <p className="truncate text-xs text-fg-muted">Email · {thread.contact}</p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close thread"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover hover:text-fg md:h-8 md:w-8"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
      <section className="border-t border-line px-5 py-4">
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-muted">Lead</h3>
        {thread.leadId ? (
          <Link to={`/leads/${encodeURIComponent(thread.leadId)}`} className="inline-flex min-h-11 items-center text-sm text-brand hover:underline md:min-h-0">
            Open {thread.name}
          </Link>
        ) : (
          <p className="text-sm text-fg-muted">Not a lead: automated mail, the team&rsquo;s own mail, or no usable address.</p>
        )}
      </section>
      <section className="min-h-0 flex-1 overflow-y-auto border-t border-line px-5 py-4">
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-muted">Mail ({thread.mails.length})</h3>
        <ol className="space-y-3">
          {thread.mails.map((m) => (
            <MailCard key={m.id} mail={m} />
          ))}
        </ol>
      </section>
    </aside>
  );
};

/**
 * Email (LEAD-2, LEAD-5, LEAD-6): what arrives at contact@whitelabels.com. A contact
 * form submission makes or revives its lead; any other mail from a person is a first
 * question and makes one too. Replies still go from Outlook: the bot does not answer
 * by email yet (REM-3, Q10).
 */
const EmailInboxPage = () => {
  const [params, setParams] = useSearchParams();
  const { data, error, loading } = useApi<InboundEmail[]>('/inbound-emails', { limit: 300 });
  const filter = (params.get('show') as Filter) || 'all';
  const selected = params.get('thread');

  const update = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        return next;
      },
      { replace: true },
    );

  const threads = threadsOf(data ?? []);
  const matches = (t: Thread, f: Filter) => f === 'all' || (f === 'form') === isForm(t);
  const count = (f: Filter) => threads.filter((t) => matches(t, f)).length;
  const shown = threads.filter((t) => matches(t, filter));
  const thread = threads.find((t) => t.key === selected);

  return (
    <div className={classNames('transition-[padding]', thread && 'xl:pr-[452px]')}>
      <PageHeader title="Conversations" description="What people write to contact@whitelabels.com: contact form messages and direct questions.">
        <ChannelNav />
      </PageHeader>
      <div className="mb-4 flex items-start gap-3">
        <p className={classNames('min-w-0 flex-1 text-sm text-fg-muted', notYet)}>
          Each message makes or updates its lead (LEAD-2, LEAD-6). The bot does not answer by email yet (REM-3, Q10): reply from Outlook for now.
        </p>
        <NotYetInfo what="mailbox" className="mt-0.5" />
      </div>
      <NotYet what="mailbox" info={false}>
        <div className="mb-4">
          <Segmented<Filter>
            label="Show"
            value={filter}
            onChange={(v) => update({ show: v === 'all' ? null : v })}
            options={[
              { value: 'all', label: `All (${count('all')})` },
              { value: 'form', label: `Contact form (${count('form')})` },
              { value: 'email', label: `Other email (${count('email')})` },
            ]}
          />
        </div>

        {error && !data ? (
          <StateMessage title="Email could not be loaded" tone="error">
            {error}
          </StateMessage>
        ) : !data ? (
          <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
        ) : shown.length === 0 ? (
          <StateMessage title="No email yet">
            Prisma reads contact@whitelabels.com once it has access (Q23). Locally, the demo mailbox fills this page.
          </StateMessage>
        ) : (
          <ul className={classNames('overflow-hidden rounded-card bg-surface shadow-e1 transition-opacity', loading && 'opacity-60')}>
            {shown.map((t) => (
              <li key={t.key} className="border-b border-line last:border-0">
                <button
                  type="button"
                  onClick={() => update({ thread: t.key })}
                  aria-current={selected === t.key}
                  className={classNames('flex w-full items-start gap-3 px-4 py-3 text-left transition-colors', selected === t.key ? 'bg-brand/10' : 'hover:bg-surface-hover')}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-inset text-xs text-fg-muted" aria-hidden>
                    {initials(t.name, t.contact)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="truncate text-fg-strong">{t.name}</p>
                      <span className="shrink-0 text-xs text-fg-faint">{formatRelative(t.last.received_at)}</span>
                    </div>
                    <p className="truncate text-sm text-fg">{t.last.subject || '(no subject)'}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
                      {isForm(t) && <span>Contact form</span>}
                      <span>{t.leadId ? 'Lead' : 'Not a lead'}</span>
                      {t.mails.length > 1 && <span>{t.mails.length} messages</span>}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </NotYet>

      {/* Outside the fade: a drawer inside it would be faded and stacked under the page's bars. */}
      {thread && <ThreadPanel thread={thread} onClose={() => update({ thread: null })} />}
    </div>
  );
};

export default EmailInboxPage;

// The All channels page lists and opens these threads too.
export { threadsOf as emailThreadsOf, isForm as isFormThread, ThreadPanel as EmailThreadPanel };
export type { Thread as EmailThread, InboundEmail };
